import {
	FIXED_PRICE_OPTIONS,
	INVOICE_BY_MAIL_OPTIONS,
	MONTHLY_BILLING_OPTIONS,
	PROJECT_BILLING_SETTINGS_INVOICE_TYPE_OPTIONS,
	SKILL_BASED_OPTIONS,
	TRAVEL_EXPENSES_INCLUDED_OPTIONS,
} from "~/constants/form.ts";
import type { CoreBillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import type {
	CentralInvoiceRecipient,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientTypes.ts";
import type { ClientContactPersonType } from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import type { Currency } from "~/modules/country/api/currency/currencyTypes.ts";
import { resolveOptionLabel } from "~/utils/form/formUtils.ts";

export type BillingSettingsLookupData = {
	currencies: Currency[] | undefined;
	invoiceRecipients: CentralInvoiceRecipient[] | undefined;
	clientContactPersons: ClientContactPersonType[] | undefined;
};

export type TableLink = {
	displayName: string;
	url: string;
};

export type ResolvedInvoiceRecipientTableLink = {
	displayName: string;
	url: string;
	type: "invoiceRecipient" | "clientContactPerson";
	typeDisplayName: string;
};

export type DivergingField<T> = {
	isDifferent: boolean;
	displayValue: T;
};

export type BillingSettingsDivergingFields = {
	invoiceTypeId: DivergingField<string>;
	isFixedPrice: DivergingField<string>;
	isSkillBased: DivergingField<string>;
	travelExpensesIncluded: DivergingField<string>;
	isBilledMonthly: DivergingField<string>;
	paymentTargetDays: DivergingField<string>;
	currencyDisplayName: DivergingField<string | null>;
	resolvedInvoiceRecipient: DivergingField<ResolvedInvoiceRecipientTableLink | null>;
	invoiceByMail: DivergingField<string>;
	additionalEmailRecipients: DivergingField<string>;
	billingInstructions: DivergingField<string | null>;
};

export type ResolvedBillingSettingsDisplay = {
	currencyDisplayName: string | null;
	emailRecipients: string[];
	invoiceRecipient: CentralInvoiceRecipient | undefined;
	clientContactPerson: ClientContactPersonType | undefined;
};

export function resolveBillingSettingsDisplay(
	settings: CoreBillingSettings,
	lookups: BillingSettingsLookupData,
): ResolvedBillingSettingsDisplay {
	const currencyDisplayName = lookups.currencies
		? (lookups.currencies.find((c) => c.id === settings.currencyId)?.displayName ?? settings.currencyId)
		: null;

	const invoiceRecipient =
		settings.invoiceRecipientId !== null && lookups.invoiceRecipients
			? lookups.invoiceRecipients.find((ir) => ir.id === settings.invoiceRecipientId)
			: undefined;

	const clientContactPerson =
		settings.clientContactPersonId !== null && lookups.clientContactPersons
			? lookups.clientContactPersons.find((ccp) => ccp.id === settings.clientContactPersonId)
			: undefined;

	return {
		currencyDisplayName,
		emailRecipients: parseEmailRecipients(settings.additionalEmailRecipients),
		invoiceRecipient,
		clientContactPerson,
	};
}

export function parseEmailRecipients(value: string | null): string[] {
	return (value ?? "")
		.split(",")
		.map((e) => e.trim())
		.filter(Boolean);
}

function buildResolvedInvoiceRecipientLink(
	settings: CoreBillingSettings,
	invoiceRecipient: CentralInvoiceRecipient | undefined,
	clientContactPerson: ClientContactPersonType | undefined,
	clientId: string,
): ResolvedInvoiceRecipientTableLink | null {
	if (settings.clientContactPersonId !== null && clientContactPerson) {
		return {
			displayName: clientContactPerson.fullName,
			url: `/contacts/${settings.clientContactPersonId}`,
			type: "clientContactPerson",
			typeDisplayName: "Ansprechpartner",
		};
	}
	if (settings.invoiceRecipientId !== null && invoiceRecipient) {
		return {
			displayName: invoiceRecipient.displayName,
			url: `/clients/${clientId}`,
			type: "invoiceRecipient",
			typeDisplayName: "Zentraler Rechnungsempfänger",
		};
	}
	return null;
}

export function computeBillingSettingsDifferences(
	orderSettings: CoreBillingSettings,
	projectSettings: CoreBillingSettings,
	lookups: BillingSettingsLookupData,
	clientId: string,
): BillingSettingsDivergingFields {
	const resolved = resolveBillingSettingsDisplay(orderSettings, lookups);
	const projectEmails = parseEmailRecipients(projectSettings.additionalEmailRecipients);

	return {
		invoiceTypeId: {
			isDifferent: orderSettings.invoiceTypeId !== projectSettings.invoiceTypeId,
			displayValue: resolveOptionLabel(
				orderSettings.invoiceTypeId,
				PROJECT_BILLING_SETTINGS_INVOICE_TYPE_OPTIONS,
			),
		},
		isFixedPrice: {
			isDifferent: orderSettings.isFixedPrice !== projectSettings.isFixedPrice,
			displayValue: resolveOptionLabel(orderSettings.isFixedPrice, FIXED_PRICE_OPTIONS),
		},
		isSkillBased: {
			isDifferent: orderSettings.isSkillBased !== projectSettings.isSkillBased,
			displayValue: resolveOptionLabel(orderSettings.isSkillBased, SKILL_BASED_OPTIONS),
		},
		travelExpensesIncluded: {
			isDifferent: orderSettings.travelExpensesIncluded !== projectSettings.travelExpensesIncluded,
			displayValue: resolveOptionLabel(
				orderSettings.travelExpensesIncluded,
				TRAVEL_EXPENSES_INCLUDED_OPTIONS,
			),
		},
		isBilledMonthly: {
			isDifferent: orderSettings.isBilledMonthly !== projectSettings.isBilledMonthly,
			displayValue: resolveOptionLabel(orderSettings.isBilledMonthly, MONTHLY_BILLING_OPTIONS),
		},
		paymentTargetDays: {
			isDifferent: orderSettings.paymentTargetDays !== projectSettings.paymentTargetDays,
			displayValue: `${orderSettings.paymentTargetDays} Tage`,
		},
		currencyDisplayName: {
			isDifferent: orderSettings.currencyId !== projectSettings.currencyId,
			displayValue: resolved.currencyDisplayName,
		},
		resolvedInvoiceRecipient: {
			isDifferent:
				orderSettings.invoiceRecipientId !== projectSettings.invoiceRecipientId ||
				orderSettings.clientContactPersonId !== projectSettings.clientContactPersonId,
			displayValue: buildResolvedInvoiceRecipientLink(
				orderSettings,
				resolved.invoiceRecipient,
				resolved.clientContactPerson,
				clientId,
			),
		},
		invoiceByMail: {
			isDifferent: orderSettings.invoiceByMail !== projectSettings.invoiceByMail,
			displayValue: resolveOptionLabel(orderSettings.invoiceByMail, INVOICE_BY_MAIL_OPTIONS),
		},
		additionalEmailRecipients: {
			isDifferent:
				resolved.emailRecipients.length !== projectEmails.length ||
				!resolved.emailRecipients.every((e) => projectEmails.includes(e)),
			displayValue: resolved.emailRecipients.join(", "),
		},
		billingInstructions: {
			isDifferent: Boolean(orderSettings.billingInstructions) && orderSettings.billingInstructions !== projectSettings.billingInstructions,
			displayValue: orderSettings.billingInstructions,
		},
	};
}